<template>
  <div>
    <PageTitle v-if="ticket" :title="$t('global.ticket') + ': ' + ticket.mask">
      <template #actions>
        <Btn variant="dangerOutline" @click="closeTicket(ticket)" v-if="allowedTicketClose">
          {{ $t("tickets.close-ticket") }}
        </Btn>
      </template>
    </PageTitle>
    <TicketBanner v-if="ticket" :ticket="ticket" />
    <Alert variant="info" :dismissible="false" v-if="isMigration">
      In order to expedite your migration, please add a comment that includes your hosting access
      USERNAME, and then use the "Shared Password" function below to share your password.
    </Alert>
    <div class="row" v-if="ticket">
      <div class="col-md-8">
        <div class="row">
          <div class="col">
            <Card title="Details" class="ticket-dialog">
              <div class="comment">
                <FormTextArea
                  v-model="newComment"
                  :disabled="submitting"
                  placeholder="Leave a comment"
                  :rows="3"
                />
                <Dropzone
                  v-model="attachmentIds"
                  ref="dropzone"
                  v-if="showAttachment"
                  @cancel="changeAttachmentSection(false)"
                  :enableCancel="true"
                />
                <div class="text-right">
                  <Btn
                    variant="primaryOutline"
                    class="mr-2"
                    @click="changeAttachmentSection(true)"
                    v-if="!showAttachment"
                    :loading="submitting"
                    >{{ $t("global.add-attachment") }}</Btn
                  >
                  <Btn
                    variant="primary"
                    @click="submitComment"
                    :loading="submitting"
                    :disabled="dropzone ? dropzone.sending : false"
                    >{{ $t("global.submit") }}</Btn
                  >
                </div>
              </div>
              <template v-if="ticket.posts">
                <div v-for="post in ticket.posts" :key="post.id" class="ticket-post">
                  <div class="ticket-post-avatar">
                    <div class="ticket-post-avatar-circle">
                      <div v-if="post.replier">
                        {{ `${post.replier.name[0]}` }}
                      </div>
                    </div>
                  </div>
                  <div class="ticket-post-content">
                    <div class="ticket-post-header">
                      <div class="ticket-post-name">{{ post.replier.name }}</div>
                      <div class="ticket-post-date date-color">
                        {{ dateFormat(post.date, true) }}
                      </div>
                    </div>
                    <div class="ticket-post-body" :class="[post.reply_by]">
                      <div class="ticket-post-text">{{ post.body }}</div>
                      <div class="ticket-post-attachments" v-if="post.attachments">
                        <div class="ticket-post-attachment-header">Attachments</div>
                        <ul class="ticket-post-attachment-list">
                          <li
                            class="ticket-post-attachment"
                            v-for="attachment in post.attachments"
                            :key="attachment.attachment_id"
                          >
                            {{ attachment.name }}
                            <Btn
                              size="small"
                              :variant="downloading ? 'primary' : 'primaryOutline'"
                              @click="downloadAttachment(attachment.attachment_id, attachment.name)"
                              :loading="downloading"
                              class="ml-1"
                            >
                              <Icon
                                icon="download"
                                :color="downloading ? 'beta' : 'gamma'"
                                :size="10"
                              />
                            </Btn>
                          </li>
                        </ul>
                      </div>
                      <div
                        v-if="post.datashare && post.datashare.views < post.datashare.maxViews"
                        class="ticket-post-datashare float-right"
                      >
                        <Btn @click="viewPassword(post.datashare)" variant="primary" size="small">
                          {{ $t("global.view-secure-data") }}
                        </Btn>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </Card>
          </div>
        </div>
      </div>
      <div class="col-md-4">
        <div class="row">
          <div class="col">
            <Card :title="$t('global.how-did-we-do')" :height100="false" class="mb-4">
              <div class="d-flex justify-content-center thumbs">
                <div @click="thumbsUp(ticket.id)">
                  <Icon
                    icon="thumbs-up"
                    :color="rating === 2 ? 'sigma' : 'kappa'"
                    :size="50"
                    alt="Thumbs Up"
                    data-value="2"
                    @click="thumbsUp(ticket.id)"
                    class="mr-3 up"
                  />
                </div>

                <div @click="thumbsDown(ticket.id)">
                  <Icon
                    icon="thumbs-down"
                    :color="rating === 1 ? 'upsilon' : 'kappa'"
                    :size="50"
                    alt="Thumbs Down"
                    data-value="1"
                    @click="thumbsDown(ticket.id)"
                    class="ml-3 down"
                  />
                </div>
              </div>
            </Card>
            <Card title="Shared Password" :height100="false" class="mb-4">
              <div class="share-new-password">
                <!-- <input type="text" v-model="passwordName" placeholder="Name" /> -->
                <FormInput v-model="password" label="Enter Password to Send" />
                <div class="text-right">
                  <Btn
                    @click="createTicketPassword"
                    :loading="passwording"
                    variant="primaryOutline"
                    size="small"
                  >
                    Create New
                  </Btn>
                </div>
              </div>
              <hr />
              <div class="shared-passwords" v-if="ticket.passwords">
                <div
                  class="shared-password"
                  v-for="password in ticket.passwords"
                  :key="password.id"
                >
                  <div class="shared-password-label">
                    <div>{{ passwordNameEval(password.identity, password.id) }}</div>
                    <div class="date-color">{{ dateFormat(password.sharedDate) }}</div>
                  </div>
                  <div>
                    <Btn
                      size="small"
                      @click="deleteTicketPassword(password.id)"
                      variant="dangerOutline"
                    >
                      <Icon icon="close" color="tau" :size="10" />
                    </Btn>
                  </div>
                </div>
              </div>
            </Card>
            <Card title="Watchers" :height100="false">
              <div class="add-recipient">
                <FormInput v-model="newRecipient" label="Email Address" />
                <div class="text-right">
                  <Btn @click="addTicketRecipient" :loading="watching" variant="primaryOutline">
                    Add
                  </Btn>
                </div>
              </div>
              <hr />
              <div class="recipient-list">
                <div class="recipient" v-for="recipient in ticket.recipients" :key="recipient.id">
                  <span class="recipient-name">{{ recipient.identity }}</span>
                  <Btn
                    @click="deleteTicketRecipient(recipient.id)"
                    v-if="ticket.recipients.length > 1"
                    :loading="watching"
                    variant="dangerOutline"
                    size="small"
                  >
                    <Icon icon="close" color="tau" :size="10" />
                  </Btn>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref } from "@vue/composition-api";
import Alert from "@/components/display/Alert";
import Btn from "@/components/display/Btn";
import { canWrite } from "@/utils/useAuth";
import Card from "@/components/display/Card";
import { closeTicket } from "@/utils/useTickets";
import ConfirmationModal from "@/components/display/ConfirmationModal";
import { dateFormat } from "leatherman-js/utils/formatters/time";
import Dropzone from "@/components/display/Dropzone2";
import FormInput from "@/components/display/form/FormInput";
import FormTextArea from "@/components/display/form/FormTextArea";
import Icon from "@/components/display/icon/Icon";
import PageTitle from "@/components/display/PageTitle";
import store from "@/store";
import TicketBanner from "@/components/smart/ticket/TicketBanner";
import ViewPassword from "@/components/smart/modals/ViewPassword";

export default {
  name: "TicketDetailPage",
  components: {
    Alert,
    Btn,
    Card,
    Icon,
    PageTitle,
    TicketBanner,
    Dropzone,
    FormInput,
    FormTextArea,
  },
  props: {
    ticketId: [Number, String],
  },
  setup(props, { root }) {
    const newComment = ref(null);
    const submitting = ref(false);
    const attachmentIds = ref([]);
    const dropzone = ref(null);
    const newRecipient = ref(null);
    const password = ref(null);
    const passwordName = ref(null);
    const showAttachment = ref(false);
    const ticket = ref(null);
    const loading = ref(true);
    const passwording = ref(false);
    const downloading = ref(false);
    const watching = ref(false);
    const rating = ref(ticket.rating);

    const changeAttachmentSection = (value) => {
      showAttachment.value = value;
    };

    const allowedTicketClose = computed(
      () => canWrite("tickets") && !ticket.value?.isClosed && ticket.value?.isCloseable
    );

    const submitComment = () => {
      if (!newComment.value) {
        return;
      }
      submitting.value = true;
      store
        .dispatch("tickets/addTicketPost", {
          id: props.ticketId,
          comment: newComment.value,
          attachmentIds: attachmentIds.value,
        })
        .then((data) => {
          ticket.value = data;
          newComment.value = null;
          attachmentIds.value = [];
          dropzone.value.removeAllFiles(true);
          showAttachment.value = false;
        })
        .finally(() => {
          submitting.value = false;
        });
    };

    const passwordNameEval = (name, id) => {
      return name || `Password ID ${id}`;
    };

    const thumbsUp = (id) => {
      store.dispatch("tickets/thumbsUp", { id: id }).then(() => {
        rating.value = 2;
        store.dispatch("toasts/addToast", {
          variant: "info",
          message: root.$t("global.thanks-for-feedback"),
        });
      });
    };

    const thumbsDown = (id) => {
      store.dispatch("tickets/thumbsDown", { id: id }).then(() => {
        rating.value = 1;
        store.dispatch("toasts/addToast", {
          variant: "info",
          message: root.$t("global.thanks-for-feedback"),
        });
      });
    };

    const addTicketRecipient = () => {
      if (!newRecipient.value) {
        return;
      }

      watching.value = true;
      store
        .dispatch("tickets/addTicketRecipient", { id: props.ticketId, email: newRecipient.value })
        .then((data) => {
          ticket.value = data;
          newRecipient.value = null;
        })
        .finally(() => {
          watching.value = false;
        });
    };

    const createTicketPassword = () => {
      if (!password.value) {
        return;
      }

      passwording.value = true;
      store
        .dispatch("tickets/createTicketPassword", {
          id: props.ticketId,
          // name: passwordName.value,
          password: password.value,
        })
        .then((data) => {
          ticket.value = data;
        })
        .finally(() => {
          passwording.value = false;
        });
    };

    const deleteTicketRecipient = (recipientId) => {
      store
        .dispatch("tickets/deleteTicketRecipient", { id: props.ticketId, recipientId })
        .then((data) => {
          ticket.value = data;
        });
    };

    const deleteTicketPassword = (passwordId) => {
      store.dispatch("modals/open", {
        template: ConfirmationModal,
        data: {
          confirm: confirmDeleteTicketPassword.bind(null, passwordId),
          errorMessage: root.$t("global.error-deleting-ticket-password"),
          message: root.$t("global.confirm-ticket-password-delete"),
          title: root.$t("global.delete-ticket-password"),
          variant: "danger",
        },
      });
    };

    const confirmDeleteTicketPassword = (passwordId) =>
      store
        .dispatch("tickets/deleteTicketPassword", { id: props.ticketId, passwordId })
        .then((data) => {
          ticket.value = data;
        });

    const downloadAttachment = (id, name) => {
      downloading.value = true;
      store.dispatch("tickets/getTicketAttachment", { id, name }).finally(() => {
        downloading.value = false;
      });
    };

    const isMigration = computed(() => {
      return process.env.VUE_APP_SUPPORT_MIGRATION_DEPARTMENT == ticket.value?.department?.id;
    });

    const viewPassword = (datashare) =>
      store.dispatch("modals/open", {
        template: ViewPassword,
        data: { password: datashare },
        close: refreshTicket,
      });

    const refreshTicket = () =>
      store.dispatch("tickets/getTicket", props.ticketId).then((data) => {
        ticket.value = data;
        rating.value = data.rating;
        loading.value = false;
      });

    onMounted(() => {
      refreshTicket();
    });

    return {
      closeTicket,
      thumbsUp,
      thumbsDown,
      ticket,
      loading,
      dateFormat,
      downloadAttachment,
      downloading,
      newComment,
      submitting,
      submitComment,
      attachmentIds,
      dropzone,
      addTicketRecipient,
      newRecipient,
      deleteTicketRecipient,
      password,
      passwordName,
      passwordNameEval,
      createTicketPassword,
      deleteTicketPassword,
      allowedTicketClose,
      changeAttachmentSection,
      showAttachment,
      passwording,
      rating,
      watching,
      isMigration,
      viewPassword,
    };
  },
};
</script>

<style lang="scss" scoped>
@import "~@/styles/variables";
.datashare {
  padding: 5px;
}

.thumbs {
  cursor: pointer;
  .up,
  .down {
    transition: all 0.2s ease-in-out !important;
  }
  .up:hover {
    transform: scale(1.2) !important;
  }
  .down:hover {
    transform: scale(1.2) !important;
  }
}

// .ticket-dialog {
//   div:last-child {
//     margin-bottom: 0.5rem;
//   }
// }
.comment {
  margin-bottom: 2rem;
}

.ticket-post {
  display: flex;
  margin-bottom: 2rem;

  .ticket-post-avatar {
    width: 30px;
    margin-right: 1rem;

    .ticket-post-avatar-circle {
      padding: 5px;
      border-style: solid;
      border-width: 1px;
      border-radius: 2rem;
      width: 30px;
      height: 30px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  .ticket-post-content {
    display: flex;
    flex-direction: column;
    flex: 1;

    .ticket-post-header {
      display: flex;
      justify-content: space-between;

      // .ticket-post-name {
      // }
      .ticket-post-date {
        font-size: 0.75rem;
      }
    }

    .ticket-post-body {
      // background-color: rgba(0, 0, 0, 0.15);
      border-style: solid;
      border-width: 1px;

      .ticket-post-text {
        padding: 1rem;
        white-space: pre-wrap;
      }
      .ticket-post-attachments {
        padding: 1rem;
        display: flex;
        flex-direction: column;
        border-top-style: solid;
        border-top-width: 1px;
        font-size: 0.75rem;

        .ticket-post-attachment-header {
          font-weight: 600;
        }

        .ticket-post-attachment-list {
          margin-bottom: 0;

          .ticket-post-attachment {
            margin-top: 0.5rem;
          }
        }
      }
      .ticket-post-datashare {
        padding: 0 1rem 1rem 1rem;
      }
    }
  }
}

.shared-passwords {
  .shared-password {
    display: flex;
    // align-items: center;
    font-size: 0.75rem;
    margin-bottom: 1rem;

    .shared-password-label {
      margin-right: 1rem;
    }
  }
}

.recipient-list {
  font-size: 0.75rem;

  .recipient {
    margin-bottom: 1rem;

    .recipient-name {
      margin-right: 1rem;
    }
  }
}
</style>
